import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDarkMode } from './DarkModeContext';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';
// HOLIDAYALLOW: the working days and the balance before and after, shared with the employee's form.
import HolidayBalanceFigures from './HolidayBalanceFigures';

const API = '/api';
const NAVY = '#1a2744';
const GOLD = '#c9a84c';

function fmt(d) {
  if (!d) return '-';
  return toDisplayDate(d).toLocaleDateString('en-GB');
}

// One approval-type section within the Validation page (see ValidationView.js) - relocated as-is
// from LeaveDeclarationForm.js's former "Holiday approvals awaiting your decision" block (built
// for HOLAPPR), now living in its own dedicated section component rather than embedded in the
// Leave/Time-Off page. Logic (pendingApprovals/decidingId state, loadPendingApprovals/decide)
// moved unchanged; this component owns its own error state instead of sharing a parent's, since a
// failed approve/reject previously had no visible error surface at all once separated from the
// declare-form's own error display.
export default function HolidayApprovalsSection() {
  const { isDarkMode } = useDarkMode();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);
  const [error, setError] = useState('');
  // Separate from `error` (which a failed approve/reject sets, and where the list must stay
  // visible): a failed LOAD must suppress the "nothing pending" empty state, so a fetch failure
  // is never mistaken for a genuinely empty approval queue. UX audit item 5 / §6.4.
  const [loadFailed, setLoadFailed] = useState(false);

  const cardBg = isDarkMode ? '#1a2744' : '#ffffff';
  const textCol = isDarkMode ? '#e8e4d8' : '#1a1a2e';
  const mutedCol = isDarkMode ? '#9ba8c0' : '#666';
  const border = isDarkMode ? '1px solid rgba(201,168,76,0.25)' : '1px solid #e2e6ec';
  const rowBorder = isDarkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #f0f0f0';

  const loadPendingApprovals = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/leave/pending-approvals`)
      .then(res => { setPendingApprovals(res.data); setError(''); setLoadFailed(false); })
      .catch(() => { setPendingApprovals([]); setError('Could not load holiday approvals - please refresh.'); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadPendingApprovals(); }, [loadPendingApprovals]);

  const decide = async (id, action) => {
    setError('');
    setDecidingId(id);
    try {
      await axios.post(`${API}/leave/${id}/${action}`);
      setPendingApprovals(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || 'Could not record this decision. Please try again.');
    } finally {
      setDecidingId(null);
    }
  };

  return (
    <div style={{ background: cardBg, border, borderRadius: '10px', padding: '28px', marginBottom: '20px' }}>
      <h3 style={{ fontSize: '15px', fontWeight: 700, color: textCol, margin: '0 0 16px' }}>Holiday approvals awaiting your decision</h3>

      {error && <div style={{ fontSize: '13px', color: '#A63A3A', marginBottom: '14px' }} role="alert" aria-live="polite">{error}</div>}

      {loading ? (
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>Loading…</p>
      ) : loadFailed ? null : pendingApprovals.length === 0 ? (
        // VALALWAYSVIS: the Validation nav item is now always visible for managers / the COO even
        // with zero pending, so this empty state is a normal, frequently-seen state (not an edge
        // case) - phrased as a clear "nothing to do right now" rather than sounding like an error.
        <p style={{ color: mutedCol, fontStyle: 'italic', fontSize: '13px', margin: 0 }}>No pending approvals right now.</p>
      ) : (
        pendingApprovals.map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: rowBorder, gap: '12px' }}>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: textCol }}>{d.employee_name}</p>
              <p style={{ margin: 0, fontSize: '12px', color: mutedCol }}>
                {fmt(d.start_date)} → {fmt(d.end_date)}
                {d.notes ? ` · ${d.notes}` : ''}
              </p>
              <HolidayBalanceFigures years={d.holiday_balance} color={mutedCol} testId="approval-balance" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => decide(d.id, 'approve')}
                disabled={decidingId === d.id}
                style={{
                  background: NAVY, color: '#fff', border: `1px solid ${GOLD}`,
                  borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                  cursor: decidingId === d.id ? 'default' : 'pointer',
                }}
              >
                {decidingId === d.id ? '…' : 'Approve'}
              </button>
              <button
                onClick={() => decide(d.id, 'reject')}
                disabled={decidingId === d.id}
                style={{
                  background: 'none', border: `1px solid ${isDarkMode ? 'rgba(166,58,58,0.5)' : '#e0b4b4'}`,
                  color: '#A63A3A', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: 700,
                  cursor: decidingId === d.id ? 'default' : 'pointer',
                }}
              >
                {decidingId === d.id ? '…' : 'Reject'}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
